/**
 * Workflows module.
 *
 * Each workflow owns its layout presets, services, components, hooks, and domain assets.
 * The visualization module remains a pure rendering engine with no workflow knowledge.
 */

import { DOSIMETRY_WORKFLOW_CONFIG, DosimetryWorkflowService } from './oncology/dosimetry';
import type { WorkflowConfig } from './types';
import { EVAR_WORKFLOW_CONFIG, EvarWorkflowService } from './vascular/evar';

import { WorkflowRegistry } from '@/visualization/workflows/workflow-registry';

// ============================================================================
// Workflow Registration
// ============================================================================

let registered = false;

/**
 * Register all built-in workflows in the global WorkflowRegistry.
 * Safe to call multiple times — only registers once.
 */
export function registerDefaultWorkflows(): void {
    if (registered) return;
    registered = true;

    WorkflowRegistry.register({
        workflowId: 'evar',
        name: 'EVAR Workflow',
        factory: (ctx) => new EvarWorkflowService(ctx),
    });

    WorkflowRegistry.register({
        workflowId: 'ai-dosimetry',
        name: 'AI Dosimetry Workflow',
        factory: (ctx) => new DosimetryWorkflowService(ctx),
    });
}

// ============================================================================
// Workflow Config Resolution
// ============================================================================

const WORKFLOW_CONFIGS: Record<string, WorkflowConfig> = {
    evar: EVAR_WORKFLOW_CONFIG,
    'ai-dosimetry': DOSIMETRY_WORKFLOW_CONFIG,
};

/**
 * Get the workflow config for a given workflow ID.
 * Returns null if the workflow is not registered.
 */
export function getWorkflowConfig(workflowId: string): WorkflowConfig | null {
    return WORKFLOW_CONFIGS[workflowId] ?? null;
}

// ============================================================================
// Re-exports
// ============================================================================

export type { WorkflowConfig } from './types';
export { COMMON_LAYOUTS, LAYOUT_SINGLE, LAYOUT_SIDE_BY_SIDE, LAYOUT_QUAD } from './common/layouts';
export { EVAR_WORKFLOW_CONFIG } from './vascular/evar';
export { DOSIMETRY_WORKFLOW_CONFIG } from './oncology/dosimetry';
